import { DoubleSide, Euler, Frustum, BufferGeometry, Float32BufferAttribute, Group, Matrix4, Mesh, PerspectiveCamera, ShaderMaterial, Sphere, Vector3, } from "three";
import { CustomShader, QuadtreeUtils } from "../../utils/internal";
import { isDefined, isNull, MultiPerspectiveTile } from "../../internal";
import { ImageQuadtree, QuadtreeDisplayMode, DisparityTile } from "./internal";
/**
 * Qubemap Quadtree Node
 *
 * @class CubemapQuadtree
 * @param {SceneGraphNode} parentNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class CubemapQuadtree extends ImageQuadtree {
    constructor(baseUrls, tileWidth = 512, imageWidth, imageHeight, pxsize = 0, minZoom = 0, maxZoom = 2, dmMaxZoom = 2, mosaic = {}, cubefaces = [], gammaAdditionConstant = 0.0) {
        super(baseUrls, tileWidth, imageWidth, imageHeight, pxsize, minZoom, maxZoom, 20);
        /**
         * list all available
         * @property {Object} tiles
         * @default {}
         */
        this.tiles = [];
        this.curZoom = 1;
        this._gamma = 1.0;
        /**
         * Additional Constant for gamma correction. Can be set according to a specific image type.
         * Default is 0.
         */
        this.gamma_addconst = 0.0;
        /**
         * The Sceneobject wich contains all tiles
         * @property {Scene} _multiPerspectiveQuadtreeScene
         */
        this._multiPerspectiveQuadtreeScene = null;
        this._tilesLoaded = false;
        this._latlonprev = [0, 0];
        this.onRotate = null;
        this.requestedImageIndex = 0;
        this.cachingStepSize = 1;
        //this could be extended later...
        this.usabilityOptions = {
            changeOpacity: false,
            opacityOfInactiveImages: 0.4,
            snapToImages: false,
        };
        this.frameskippedcbf = null;
        this.lod0timer = -1;
        this.camInfo = [];
        this._cubefaces = [];
        this.lbboundries = {
            latMax: -1,
            latMin: -1,
            longMin: -1,
            longMax: -1,
        };
        /**
         * list all available
         * @property {Object} tiles
         * @default {}
         */
        this.disptiles = [];
        /**
         * Holds all registered disptileready callback functions
         * @type {Array}
         */
        this.disptilesreadycbf = [];
        /**
         * Holds all registered tileemptyready callback functions
         * @type {Array}
         */
        this.disptilesemptyreadycbf = [];
        /**
         * Holds all visible disptiles
         * @property {Tile[]} lstFrustumDispTiles
         * @default []
         */
        this.lstFrustumDispTiles = [];
        this.dmMaxZoom = 2;
        this._mosaic = {};
        this.dmMaxZoom = dmMaxZoom;
        this._mosaic = mosaic;
        this._cubefaces = cubefaces;
        this.lbboundries["latMax"] = Math.max(...Object.keys(this._mosaic)
            .filter((o) => this._mosaic[o] !== undefined)
            .map((o) => this._mosaic[o].latmax), 0);
        this.lbboundries["latMin"] = Math.min(...Object.keys(this._mosaic)
            .filter((o) => this._mosaic[o] !== undefined)
            .map((o) => this._mosaic[o].latmin), 0);
        this.lbboundries["longMin"] = Math.min(...Object.keys(this._mosaic)
            .filter((o) => this._mosaic[o] !== undefined)
            .map((o) => this._mosaic[o].longmin), 0);
        this.lbboundries["longMax"] = Math.max(...Object.keys(this._mosaic)
            .filter((o) => this._mosaic[o] !== undefined)
            .map((o) => this._mosaic[o].longmax), 0);
        for (let i = 0; i < this._cubefaces.length; i++) {
            const euler = new Euler((this._cubefaces[i].position.domega * Math.PI) / 180, (this._cubefaces[i].position.dphi * Math.PI) / 180, (this._cubefaces[i].position.dkappa * Math.PI) / 180);
            const rotmat = new Matrix4().makeRotationFromEuler(euler);
            const camera = new PerspectiveCamera(90.0, 1.0, 1, 1000);
            camera.setRotationFromMatrix(rotmat);
            camera.updateProjectionMatrix();
            camera.updateMatrixWorld(true);
            this._cubefaces[i].Rr = rotmat;
            this._cubefaces[i].camera = camera;
        }
        this.gamma_addconst = gammaAdditionConstant;
    }
    init(traversalState) {
        this._multiPerspectiveQuadtreeScene = traversalState.scene.tMultiPerspectiveScene;
        super.init(traversalState);
        this.createMultiPerspectiveTiles();
        this._imageChanged = true;
        for (let i = 0; i < this._cubefaces.length; i++) {
            this.disptiles.push({});
            this.disptilesreadycbf.push({});
            this.disptilesemptyreadycbf.push({});
        }
        //vgl. zoom calculation in render function
        const focallength = this.imageHeight / 2.0;
        const ch = traversalState.scene.engine.getContextHeight();
        let minfov = (ch * this.imageHeight) / (this.maxZoom * 2.0 * focallength * 2 * this.tileWidth);
        minfov = Math.atan(minfov);
        minfov *= 180.0 / Math.PI;
        //dont set the minfov higher than 10 degrees. Even if image resolution is actually to low.
        minfov = Math.min(minfov, 10);
        traversalState.cameras["multiperspectiveCamera"].setFovLimits(minfov);
    }
    destroy() {
        super.destroy();
        this._wipeDispTiles();
        this.currentImageIndex = -1;
    }
    /**
     * @method render
     *
     * reads:   traversalState.lockRendering
     *          traversalState.cameras
     *          traversalState.panoCamChanged
     *          traversalState.gamma
     *          traversalState.actualMousePos
     *
     * writes:  traversalState.actualQuadtree
     *
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        super.render(traversalState);
        if (this.hide || this.currentImageIndex == -1)
            return;
        this.currentTraversalState = traversalState;
        if (this._cacheNotReadyEventSent)
            traversalState.lockRendering = true;
        // --- Measure view quadtree is active
        if (this.active) {
            if (this._cacheNotReadyEventSent)
                traversalState.lockRendering = true;
            const camera = traversalState.cameras["multiperspectiveCamera"].iCamera;
            //check if panocamera was rotated an call callback and determine the actual main-cam
            if (traversalState.panoCamChanged) {
                const w = this.currentTraversalState.scene.engine.getContextWidth();
                const h = this.currentTraversalState.scene.engine.getContextHeight();
                const pickedmap = this.mouseToMap(w / 2, h / 2);
                this.faceindex = pickedmap[3];
                const latlon = traversalState.cameras["multiperspectiveCamera"].getLatLon();
                if ((latlon[0] != this._latlonprev[0] ||
                    latlon[1] != this._latlonprev[1] ||
                    latlon[2] != this._latlonprev[2]) &&
                    this.onRotate != null) {
                    this.onRotate(latlon[0], latlon[1], latlon[2]);
                    this._latlonprev = latlon;
                }
            }
            // Render
            if (this.baseUrls[this.currentImageIndex] != "" && this.baseUrls[this.currentImageIndex] != undefined) {
                // Calculate optimized frustum list:
                const fovY = camera.fov / (180.0 / Math.PI);
                const frustum = new Frustum();
                frustum.setFromProjectionMatrix(new Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
                //calculation of zoom
                //focallength is half the image size, becaus its a cube.
                const focallength = this.imageHeight / 2.0;
                // nPixels = Number of pixels that could be loaded for the current field of view
                const nPixels = 2.0 * Math.tan(fovY / 2.0) * focallength;
                // with of the context
                const ch = traversalState.scene.engine.getContextHeight();
                let zoom = ((ch / (this.tileWidth * 2)) * this.imageHeight) / nPixels;
                zoom = Math.min(Math.max(Math.ceil(zoom), 1), this.maxZoom);
                this.curZoom = this._forceLod0 ? 0 : zoom > 0 ? zoom : 0;
                this._allTilesReady = true;
                //if image has changed delete all tiles from scene
                if (this._imageChanged)
                    this.wipeTileTextures();
                //remove all tiles
                for (let i = 0; i < this._cubefaces.length; i++) {
                    for (let lstI = 0; lstI < this.lstFrustumTiles[i].length; lstI++) {
                        this._multiPerspectiveQuadtreeScene.remove(this.lstFrustumTiles[i][lstI].tMesh);
                    }
                }
                //add lod 0 tiles
                for (let i = 0; i < this._cubefaces.length; i++) {
                    this.lstFrustumTiles[i] = [];
                    this.createFrustumList(0, frustum, i);
                }
                for (let i = 0; i < this._cubefaces.length; i++) {
                    if (this.faceindex > -1) {
                        for (let ik = 0; ik <= this.curZoom; ik++) {
                            this.createFrustumList(ik, frustum, i);
                        }
                    }
                    for (let xi = 0; xi < this.lstFrustumTiles[i].length; xi++) {
                        if (this.lstFrustumTiles[i][xi].tileReady) {
                            const t = this.lstFrustumTiles[i][xi];
                            this._multiPerspectiveQuadtreeScene.add(t.tMesh);
                            if (i != this.faceindex && this.usabilityOptions.changeOpacity) {
                                //change opacity
                                t.tMesh.material.needsUpdate = true;
                                t.tMesh.material.uniforms["uOpacity"] = {
                                    value: this.usabilityOptions.opacityOfInactiveImages,
                                };
                            }
                            else {
                                t.tMesh.material.needsUpdate = true;
                                t.tMesh.material.uniforms["uOpacity"] = { value: 1.0 };
                            }
                        }
                        if (!(this.lstFrustumTiles[i][xi].tileReady &&
                            this.lstFrustumTiles[i][xi].initialized &&
                            this.lstFrustumTiles[i][xi].tMesh.material.uniforms.uTexture != undefined)) {
                            this._allTilesReady = false;
                        }
                    }
                }
                //add the _actualcam now
                // to be sure, that actualmultiperspectivetile is last added so its in front!
                for (let xi = 0; xi < this.lstFrustumTiles[this.faceindex].length; xi++) {
                    if (this.lstFrustumTiles[this.faceindex][xi].tileReady) {
                        this._multiPerspectiveQuadtreeScene.add(this.lstFrustumTiles[this.faceindex][xi].tMesh);
                    }
                }
                //check if all visible tiles are ready
                if (!this._allTilesReady && this._allTilesReadyPrev) {
                    //first cycle in which some tiles are not ready
                    if (this.visibleTilesNotReady)
                        this.visibleTilesNotReady();
                }
                if (this._allTilesReady && !this._allTilesReadyPrev) {
                    //first cycle in which all tiles are ready loaded again
                    if (this.visibleTilesReady)
                        this.visibleTilesReady();
                }
                this._allTilesReadyPrev = this._allTilesReady;
                if (this._forceLod0 != this._forceLod0prev) {
                    this.requestedImageIndex = this.currentImageIndex;
                    if (!isNull(this.cacheNotReadyEvent) && this._cacheNotReadyEventSent) {
                        this.cacheReadyEvent();
                        this._cacheNotReadyEventSent = false;
                    }
                }
                this._forceLod0prev = this._forceLod0;
                //caching stuff when switching to next frame
                if (this._imageChanged) {
                    this._imageChanged = false;
                    if (this.framechangedcbf != null)
                        this.framechangedcbf(this.currentImageIndex);
                }
                if (!this._allTilesReady)
                    traversalState.lockRendering = true;
            }
            this._gamma = traversalState.gamma;
        }
        for (let i = 0; i < this._cubefaces.length; i++) {
            this.lstFrustumDispTiles[i] = [];
            for (let lstI = 0; lstI < this.lstFrustumTiles[i].length; lstI++) {
                const mptile = this.lstFrustumTiles[i][lstI];
                const lod = QuadtreeUtils.QuadKeyToTileCoord(mptile.quadCode)["lod"];
                // if _forceLod0
                if (mptile.quadCode != "" && lod <= this.dmMaxZoom) {
                    const disptile = this.getDisparityTile(mptile.quadCode, mptile.cameraIndex);
                    this.lstFrustumDispTiles[i].push(disptile);
                }
            }
        }
    }
    latlonToSensorId(latlon) {
        if (latlon.length !== 2)
            return -1;
        let sensorid = -1;
        const lat = latlon[0];
        const lon = latlon[1];
        const mod = (n, m) => {
            return ((n % m) + m) % m;
        };
        for (const key in this._mosaic) {
            const current = this._mosaic[key];
            const shift = mod(current["longmin"], 360);
            const _longmax = mod(current["longmax"] - shift, 360);
            const _lon = mod(lon - shift, 360);
            if (_lon > _longmax)
                continue;
            if (lat < current["latmin"])
                continue;
            if (lat > current["latmax"])
                continue;
            sensorid = parseInt(key);
            break;
        }
        return sensorid;
    }
    createFrustumList(zoomLevel, frustum, cameraIdx) {
        if (this.curZoom == 0 && cameraIdx != this.faceindex && this._forceLod0)
            return;
        const maxZoomLevel = this.maxZoom;
        for (let xx = 0; xx < Math.pow(2, zoomLevel); xx++) {
            for (let yy = 0; yy < Math.pow(2, zoomLevel); yy++) {
                const tileToAddQuadKey = QuadtreeUtils.TileCoordToQuadKey(xx, yy, zoomLevel);
                const tileToAddCoord = QuadtreeUtils.QuadKeyToTileCoord(tileToAddQuadKey);
                const tileToAdd = this.tiles[cameraIdx][tileToAddQuadKey];
                if (this.currentTraversalState.gamma != this._gamma)
                    tileToAdd.tMesh.material.uniforms["uGamma"] = {
                        value: this.currentTraversalState.gamma + this.gamma_addconst,
                    };
                // Check if bottom layer is visible, more accurate boundingsphere check
                const children = QuadtreeUtils.GetChildrenAt(tileToAddQuadKey, maxZoomLevel);
                for (let cI = 0; cI < children.length; cI++) {
                    const quadKey = children[cI];
                    const tile = this.tiles[cameraIdx][quadKey];
                    const camI = this._cubefaces[tile.cameraIndex];
                    const geometry = this.tiles[cameraIdx][quadKey].tGeometry;
                    const objTransform = new Group();
                    objTransform.rotation.set((camI.position.domega * Math.PI) / 180, (camI.position.dphi * Math.PI) / 180, (camI.position.dkappa * Math.PI) / 180);
                    objTransform.setRotationFromMatrix(camI.Rr);
                    const pos = new Vector3(camI.position.dx, camI.position.dy, camI.position.dz);
                    objTransform.position.set(pos.x, pos.y, pos.z);
                    objTransform.updateMatrixWorld(true);
                    if (geometry.boundingSphere === null)
                        geometry.computeBoundingSphere();
                    const center = new Vector3().copy(geometry.boundingSphere.center);
                    const sphere = new Sphere(center, geometry.boundingSphere.radius);
                    sphere.applyMatrix4(objTransform.matrixWorld);
                    if (frustum.intersectsSphere(sphere) === true) {
                        if (!tileToAdd.initialized) {
                            const url = this.baseUrls[this.currentImageIndex] +
                                "/" +
                                tileToAdd.cameraIndex +
                                "/?" +
                                "lod=" +
                                tileToAddCoord["lod"] +
                                "&x=" +
                                tileToAddCoord["x"] +
                                "&y=" +
                                tileToAddCoord["y"];
                            tileToAdd.loadTexture(url);
                        }
                        this.lstFrustumTiles[cameraIdx].push(tileToAdd);
                        break;
                    }
                }
            }
        }
    }
    /**
     * create all pegasustiles in an initial step
     */
    createMultiPerspectiveTiles() {
        this.wipeTiles();
        for (let i = 0; i < this._cubefaces.length; i++) {
            this.tiles.push({});
            this.lstFrustumTiles.push([]);
            this.lstFrustumDispTiles.push([]);
            // create camera geometries
            for (let lod = 0; lod < this.maxZoom + 1; lod++) {
                if (lod > 0) {
                    for (let xx = 0; xx < Math.pow(2, lod); xx++) {
                        for (let yy = 0; yy < Math.pow(2, lod); yy++) {
                            const quadKey = QuadtreeUtils.TileCoordToQuadKey(xx, yy, lod);
                            this.tiles[i][quadKey] = this._generateTile(quadKey, i);
                        }
                    }
                }
                else {
                    this.tiles[i][""] = this._generateTile("", i);
                }
            }
        }
        this._tilesLoaded = true;
    }
    /**
     * clean up all textures e.g. when frame changes
     */
    wipeTileTextures() {
        for (let i = 0; i < this._cubefaces.length; i++) {
            for (const key in this.tiles[i]) {
                this.tiles[i][key].initialized = false;
                this.tiles[i][key].tileReady = false;
                this._multiPerspectiveQuadtreeScene.remove(this.tiles[i][key].tMesh);
                this.tiles[i][key].destroy();
            }
        }
    }
    /**
     * called to destroy everything to release memory
     * @method wipeTiles
     */
    wipeTiles() {
        if (this._tilesLoaded) {
            for (let i = 0; i < this._cubefaces.length; i++) {
                for (const key in this.tiles[i]) {
                    this.tiles[i][key].initialized = false;
                    this.tiles[i][key].tileReady = false;
                    this._multiPerspectiveQuadtreeScene.remove(this.tiles[i][key].tMesh);
                    this.tiles[i][key].destroy();
                }
            }
            this._tilesLoaded = false;
        }
        this.tiles = [];
        super.wipeTiles();
        this.lstFrustumTiles = [];
    }
    /**
     * Wipes all disparity tiles from catalog, frustum and scene. this is used
     * before loading a new tiles image
     * @method _wipeDispTiles
     */
    _wipeDispTiles() {
        for (let i = 0; i < this._cubefaces.length; i++) {
            for (let j = 0; j < this.lstFrustumDispTiles[i].length; j++) {
                // clear frustum
                this.lstFrustumDispTiles[i] = [];
            }
            // clear tile catalog
            for (const qc in this.disptiles[i]) {
                if (this.disptiles[i][qc] != null)
                    this.disptiles[i][qc].destroy();
                this.disptiles[i][qc] = null;
                this.disptilesreadycbf[i][qc] = null;
                this.disptilesemptyreadycbf[i][qc] = null;
            }
        }
    }
    /**
     * Read the pixel color value in RGBA [0.0 .. 1.0]
     * @method readPixelValue
     * @param {number} x
     * @param {number} y
     * @param {boolean} absolute
     * @return {number[]}
     */
    readPixelValue(x, y, absolute) {
        return [0.0, 0.0, 0.0];
    }
    /**
     * Wipe all active tiles and set the current quadtree image set
     * @method setImageIndex
     * @param {number} index
     *
     */
    setImageIndex(index, playmode = false, cachingstepsize) {
        if (this.currentImageIndex == -1)
            this._cacheNotReadyEventSent = true; //first call throw cachereadyevent
        this._wipeDispTiles();
        this.determineDisplayMode(index, playmode);
        this.requestedImageIndex = index;
        this.cachingStepSize = cachingstepsize || 1;
        // special case if it's the first time a framenumber is called.
        // Send cache not ready to disable waitingWheel...
        if (this.displayMode == QuadtreeDisplayMode.PLAYINGFORWARD ||
            this.displayMode == QuadtreeDisplayMode.PLAYINGBACKWARD ||
            this.currentImageIndex == -1) {
            this.forceLod0(true);
            this._cacheSequence();
            // is cache ready? then change image directly
            if (this._cachingCompleted) {
                this.changeToRequestedImage();
            }
            else {
                if (!isNull(this.cacheNotReadyEvent) && !this._cacheNotReadyEventSent) {
                    this.cacheNotReadyEvent();
                    this._cacheNotReadyEventSent = true;
                }
            }
        }
        else {
            switch (this.displayMode) {
                case QuadtreeDisplayMode.FRAMESTEPPINGFORWARD:
                case QuadtreeDisplayMode.FRAMESTEPPINGBACKWARD:
                    this.forceLod0(true);
                    this.changeToRequestedImage();
                    this._cacheSequence();
                    break;
                default:
                    break;
            }
        }
        // check if image was not changed within 200ms so load tiles
        const lod0cbf = () => {
            if (!playmode &&
                !(this.displayMode == QuadtreeDisplayMode.FRAMESLIDINGFORWARD) &&
                !(this.displayMode == QuadtreeDisplayMode.FRAMESLIDINGBACKWARD)) {
                this.forceLod0(false);
            }
            this.currentTraversalState.scene.render();
        };
        window.clearTimeout(this.lod0timer);
        this.lod0timer = window.setTimeout(() => {
            lod0cbf();
        }, 200);
    }
    setActive(active) {
        this.active = active;
        if (isDefined(this.iMesh) && this.iMesh != null)
            this.iMesh.visible = active;
    }
    _createTileGeometry(quadCode) {
        const tileCoord = QuadtreeUtils.QuadKeyToNormalizedCoord(quadCode);
        tileCoord[0] = (tileCoord[0] - 0.5) * 2;
        tileCoord[1] = (-tileCoord[1] + 0.5) * 2;
        tileCoord[2] = (tileCoord[2] - 0.5) * 2;
        tileCoord[3] = (-tileCoord[3] + 0.5) * 2;
        const geom = new BufferGeometry();
        const vertices = [
            tileCoord[0],
            tileCoord[1],
            -1, // 0
            tileCoord[0],
            tileCoord[3],
            -1, // 1
            tileCoord[2],
            tileCoord[3],
            -1, // 2
            tileCoord[2],
            tileCoord[1],
            -1, // 3
        ];
        const indices = [0, 1, 2, 0, 2, 3];
        const uvs = [
            0,
            1, // 0
            0,
            0, // 1
            1,
            0, // 2
            1,
            1, // 3
        ];
        geom.setIndex(indices);
        geom.setAttribute("position", new Float32BufferAttribute(vertices, 3));
        geom.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
        geom.computeVertexNormals();
        geom.computeBoundingSphere();
        geom.computeBoundingBox();
        return geom;
    }
    _generateTile(quadCode, cubefaceindex) {
        const camI = this._cubefaces[cubefaceindex];
        const geometry = this._createTileGeometry(quadCode);
        const extent = QuadtreeUtils.QuadKeyToNormalizedCoord(quadCode);
        const material = new ShaderMaterial({
            transparent: true,
            side: DoubleSide,
            wireframe: false,
            opacity: 1.0,
            uniforms: {
                uGamma: { value: this._gamma + this.gamma_addconst },
                uTexture: { value: null },
                fCalloteId: { value: cubefaceindex },
                fLod: { value: quadCode.length },
                uOpacity: { value: 1.0 },
            },
            vertexShader: CustomShader.tileVertexShader,
            fragmentShader: CustomShader.tileFragmentShader,
            depthWrite: false,
            depthTest: false,
        });
        const tMesh = new Mesh(geometry, material);
        tMesh.renderOrder = quadCode.length;
        tMesh.setRotationFromMatrix(camI.Rr);
        return new MultiPerspectiveTile(tMesh, geometry, cubefaceindex, quadCode, extent, false, this);
    }
    /**
     * @method _checkAllTilesReady to handle tile ready events...
     * @param camId
     * @param quadCode
     * @returns {boolean}
     * @private
     */
    _checkAllTilesReady(camId, quadCode) {
        if (quadCode != "") {
            for (let i = 0; i < 4; i++) {
                const qC = quadCode.slice(0, -1) + i;
                if (!this.tiles[camId][qC].tileReady)
                    return false;
            }
        }
        return true;
    }
    changeToRequestedImage() {
        if (this._cacheNotReadyEventSent) {
            if (!isNull(this.cacheReadyEvent)) {
                this.cacheReadyEvent();
                this._cacheNotReadyEventSent = false;
            }
        }
        if (this.currentImageIndex != this.requestedImageIndex) {
            this.currentImageIndex = this.requestedImageIndex;
            this._imageChanged = true;
            this.currentTraversalState.scene.render();
        }
    }
    /**
     * Get actual map coordinates: [0...1]
     * https://github.com/iNovitas/infraVIS_SDK/wiki/Coordinate-Systems#map-coordinates
     * and actual image plane Id.
     *
     * @param mx  mouse_x
     * @param my  mouse_y
     * @returns {any} [nx,ny,sensorid, cubefaceindex, cubeface position]
     */
    mouseToMap(mx, my) {
        //calc camera-image coordinages
        const x = 2 * (mx / this.currentTraversalState.scene.engine.getContextWidth()) - 1;
        const y = 1 - 2 * (my / this.currentTraversalState.scene.engine.getContextHeight());
        const z = -1.0;
        const r = new Vector3(x, y, z);
        const actualCamera = this.currentTraversalState.actualCamera.iCamera;
        // world-origin --> point.
        // Unproject includes perspective --> ortho-Projection and Transformation into the Wolrd
        r.unproject(actualCamera);
        for (let i = 0; i < this._cubefaces.length; i++) {
            //project the point into each camera (cube-plane)
            const r2 = r.clone();
            r2.project(this._cubefaces[i].camera);
            //check if the point is in the image or not
            if (r2.z < 0) {
                if (r2.x >= -1.0 && r2.x <= 1.0 && r2.y >= -1.0 && r2.y <= 1.0) {
                    const mapx = 0.5 + r2.x * 0.5;
                    const mapy = 0.5 - r2.y * 0.5;
                    const coords3D = this.mapTo3D(mapx, mapy, i);
                    const lat = Math.atan(coords3D.y / Math.sqrt(Math.pow(coords3D.x, 2) + Math.pow(coords3D.y, 2))) *
                        (180 / Math.PI);
                    const lon = Math.atan2(coords3D.x, coords3D.z * -1) * (180 / Math.PI);
                    const sensorid = this.latlonToSensorId([lat, lon]);
                    return [mapx, mapy, sensorid, i, this._cubefaces[i].position];
                }
            }
        }
        return [0, 0, 0, 0, this._cubefaces[0].position];
    }
    // public mouseToModel(mx: number, my: number): Vector3 {
    //     const xoffset = (this.imageHeight - this.imageWidth) / this.imageHeight / 2;
    //     const ppX = (mx + xoffset - 0.5) * this.imageHeight * this.pixelsize * 1000;
    //     const ppY = -(my - 0.5) * this.imageHeight * this.pixelsize * 1000;
    //     const ppZ = -this._cubefaces[sensorId].focallength;
    //     const v = new Vector3(ppX, ppY, ppZ);
    //     const camI = this._cubefaces[sensorId];
    //     const mat = new Matrix4().makeRotationFromEuler(
    //         new Euler(
    //             (camI.position.domega * Math.PI) / 180,
    //             (camI.position.dphi * Math.PI) / 180,
    //             (camI.position.dkappa * Math.PI) / 180
    //         )
    //     );
    // TODO RB: use disp. for z
    //     const pos = new Vector3(camI.position.dx, camI.position.dy, camI.position.dz);
    //     mat.setPosition(pos);
    //     v.applyMatrix4(mat);
    //     return v;
    // }
    /**
     * Calculates mouse coordinates from map coordinates.
     * @param nx
     * @param ny
     * @param sensorid
     * @returns {number[]}
     */
    mapToMouse(nx, ny, sensorid = 0) {
        const pos3d = new Vector3(nx * 2 - 1.0, 1.0 - 2 * ny, -1.0);
        pos3d.unproject(this._cubefaces[sensorid].camera);
        pos3d.project(this.currentTraversalState.actualCamera.iCamera);
        const w = this.currentTraversalState.scene.engine.getContextWidth() / 2;
        const h = this.currentTraversalState.scene.engine.getContextHeight() / 2;
        pos3d.x = pos3d.x * w + w;
        pos3d.y = -(pos3d.y * h) + h;
        return [pos3d.x, pos3d.y];
    }
    /**
     * Get actual 3d coordinates in sensor system
     * @returns {number[]}
     */
    mapTo3D(nx, ny, cubefaceindex) {
        const ppX = nx - 0.5;
        const ppY = -(ny - 0.5);
        const ppZ = -0.5;
        const v = new Vector3(ppX, ppY, ppZ);
        const camI = this._cubefaces[cubefaceindex];
        v.applyMatrix4(camI.Rr);
        return v;
    }
    faceToOrientation(faceindex) {
        return this._cubefaces[faceindex].position;
    }
    setCacheRange(cacherange) {
        this._cachingRange = cacherange;
    }
    /**
     * Request a disparity tile and register tileready and tileemptyready callback.
     * Writes the disparity tile to the disptiles-array.
     * Multiple callbacks can be registered and emited.
     * @param quadcode
     * @param sensorid
     * @param tileready
     * @param tileemptyready
     */
    getDisparityTile(quadcode, sensorid, tileready, tileemptyready) {
        if (!isDefined(this.disptiles[sensorid][quadcode]) || this.disptiles[sensorid][quadcode] == null) {
            //create a new disparity tile if not existant
            const tileReady = (tile) => {
                //emit registered tileready-callback functions
                for (let i = 0; i < this.disptilesreadycbf[tile.sensorid][tile.quadCode].length; i++) {
                    this.disptilesreadycbf[tile.sensorid][tile.quadCode][i](tile);
                }
                if (tileready)
                    tileready(tile);
            };
            const tileEmptyReady = (tile) => {
                //emit registered tileemptyready-callback functions
                for (let i = 0; i < this.disptilesemptyreadycbf[tile.sensorid][tile.quadCode].length; i++) {
                    this.disptilesemptyreadycbf[tile.sensorid][tile.quadCode][i](tile);
                }
                if (tileemptyready)
                    tileemptyready(tile);
            };
            const url = this.baseUrls[this.currentImageIndex] + "/" + sensorid;
            this.disptilesreadycbf[sensorid][quadcode] = [];
            this.disptilesemptyreadycbf[sensorid][quadcode] = [];
            this.disptiles[sensorid][quadcode] = new DisparityTile(quadcode, url, this.imageWidth, this.imageHeight, this.tileWidth, this.currentTraversalState, tileReady, tileEmptyReady, sensorid);
        }
        if (this.disptiles[sensorid][quadcode].isAvailable()) {
            //emit tileready if the disptile is already loaded (e.g. the dispmap had been loaded from cache)
            if (tileready)
                tileready(this.disptiles[sensorid][quadcode]);
        }
        else {
            if (tileemptyready)
                tileemptyready(this.disptiles[sensorid][quadcode]);
        }
        return this.disptiles[sensorid][quadcode];
    }
    /**
     * Read and return the disparity value
     * @param {number} map_x
     * @param {number} map_y
     * @param {number} sensorid
     * @return {number[]} array containing [disparity ,mapx, mapy, quality]
     */
    readDisparity(map_x, map_y, sensorid) {
        const cid = sensorid;
        for (let i = 0; i < this.lstFrustumDispTiles[cid].length; i++) {
            const tile = this.lstFrustumDispTiles[cid][i];
            if (map_x >= tile.extent[0] &&
                map_x < tile.extent[2] &&
                map_y >= tile.extent[1] &&
                map_y < tile.extent[3]) {
                // point is inside this tile!
                const retVal = tile.getDisparityValue(map_x, map_y);
                return [retVal[0], map_x, map_y, retVal[1]];
            }
        }
        // nothing found, return no data value
        return [0, map_x, map_y, 0];
    }
    pos3dtoMap(pos3d, cubefaceindex) {
        const camI = this._cubefaces[cubefaceindex];
        const mat = new Matrix4().makeRotationFromEuler(new Euler((camI.position.domega * Math.PI) / 180, (camI.position.dphi * Math.PI) / 180, (camI.position.dkappa * Math.PI) / 180));
        const matInv = new Matrix4().copy(mat).invert();
        pos3d.applyMatrix4(matInv);
        const xoffset = (this.imageHeight - this.imageWidth) / this.imageHeight / 2;
        const nX = pos3d.x + 0.5;
        const nY = -1 * pos3d.y + 0.5;
        return new Vector3(nX, nY, -0.5);
    }
}
//# sourceMappingURL=cubemapquadtree.js.map